/**
 * Per-device "last seen" for patient bid-history threads so sidebar / KPI badges
 * only reflect inbound activity the patient has not opened yet.
 */

const STORAGE_KEY = "healbid_patient_chat_last_seen_at_v1";

function readRawMap() {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (!s) return {};
    const o = JSON.parse(s);
    return o && typeof o === "object" && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

function writeRawMap(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {}
}

function parseSeenAt(val) {
  const n = Number(val);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

/** Last time the patient acknowledged this thread (epoch ms). */
export function getPatientChatSeenAtMs(conversationId) {
  const id = String(conversationId || "").trim();
  if (!id) return 0;
  return parseSeenAt(readRawMap()[id]);
}

/**
 * Mark a thread read at least through `atMsOrIso` (usually conversation.lastMessageAt or latest message time).
 * Fires `healbid_patient_chat_read` so dashboards can refresh badge counts without a full reload.
 */
export function markPatientChatThreadSeen(conversationId, atMsOrIso) {
  const id = String(conversationId || "").trim();
  if (!id) return;
  let ts =
    typeof atMsOrIso === "number"
      ? atMsOrIso
      : new Date(atMsOrIso == null ? Date.now() : atMsOrIso).getTime();
  if (!Number.isFinite(ts) || ts <= 0) ts = Date.now();
  const map = readRawMap();
  const prev = parseSeenAt(map[id]);
  map[id] = String(Math.max(prev, ts));
  writeRawMap(map);
  try {
    window.dispatchEvent(new CustomEvent("healbid_patient_chat_read"));
  } catch (_) {}
}

/** True if last inbox line is from someone other than the patient (doctor / hospital / admin / system). */
function isInboundLastSender(lastMessageSenderRole) {
  const r = String(lastMessageSenderRole || "").toLowerCase();
  if (!r) return true;
  return r !== "user" && r !== "patient";
}

/**
 * Count conversations whose latest inbound update is newer than this device's last "seen" time.
 */
export function countPatientUnreadChatThreads(conversations) {
  if (!Array.isArray(conversations)) return 0;
  const map = readRawMap();
  let n = 0;
  for (const c of conversations) {
    const id = String(c?._id || c?.id || "").trim();
    if (!id) continue;
    const lastAt = new Date(c?.lastMessageAt || 0).getTime();
    if (!Number.isFinite(lastAt) || lastAt <= 0) continue;
    if (!isInboundLastSender(c?.lastMessageSenderRole)) continue;
    const seen = parseSeenAt(map[id]);
    if (lastAt > seen) n += 1;
  }
  return Math.min(99, n);
}

/** True when the latest inbound message is newer than this device's last open time for the thread. */
export function isPatientChatThreadUnread(conversation) {
  if (!conversation) return false;
  const id = String(conversation?._id || conversation?.id || "").trim();
  if (!id) return false;
  const lastAt = new Date(conversation?.lastMessageAt || 0).getTime();
  if (!Number.isFinite(lastAt) || lastAt <= 0) return false;
  if (!isInboundLastSender(conversation?.lastMessageSenderRole)) return false;
  const seen = parseSeenAt(readRawMap()[id]);
  return lastAt > seen;
}
